// packages/ui/src/components/SmoothCollapse.tsx
'use client';

import { useRef, type ReactNode } from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { Transition, type TransitionStatus } from 'react-transition-group';

/**
 * CSS-grid based collapse. Use this instead of MUI `Collapse` for any content
 * that animates in/out in this codebase.
 *
 * **Why not MUI Collapse?** MUI measures the content's pixel height at the
 * start of the transition and animates `height: 0px → 312px` (or whatever was
 * measured), then swaps to `height: auto` at the end. If the content reflows
 * mid-animation (async data, image load, font swap) the measured value
 * disagrees with the final `auto` height and you see a visible jump at the
 * end. `SmoothCollapse` animates `grid-template-rows: 0fr ↔ 1fr` (or
 * `grid-template-columns` for horizontal), which resolves to the content's
 * *current* natural size on every frame — no measurement, no
 * end-of-transition swap, no jump.
 *
 * **API.** The exposed props are the ones at least one callsite needs. If you
 * need additional behavior (e.g. `collapsedSize`, custom easing), **add it to
 * this component** — do not import MUI `Collapse` as a workaround. The
 * underlying `react-transition-group` `Transition` already supports a wider
 * surface; surface more of it here as needs arise.
 *
 * **TransitionGroup compatibility.** The lifecycle callbacks (`onEntered`,
 * `onExited`, etc.) and the `appear`/`enter`/`exit` toggles are forwarded so
 * `<TransitionGroup>` can drive enter/exit animations on a list of items and,
 * critically, get the `onExited` callback it needs to unmount removed items.
 *
 * **Browser floor:** Chrome 107+, Firefox 110+, Safari 16+ (animatable
 * `<integer>fr` row/column tracks). Older browsers still toggle between
 * states but without the smooth transition.
 */

type Orientation = 'vertical' | 'horizontal';

const DEFAULT_TIMEOUT_MS = 300;
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

interface SmoothCollapseProps {
  children: ReactNode;
  /** Whether the content is shown. Defaults to `false` (collapsed). */
  in?: boolean;
  /** Animate on initial mount. `TransitionGroup` injects this for entering items. */
  appear?: boolean;
  /** Run the enter transition. Defaults to true. */
  enter?: boolean;
  /** Run the exit transition. Defaults to true. */
  exit?: boolean;
  /** Don't render children until first `in={true}`. */
  mountOnEnter?: boolean;
  /** Unmount children after the exit transition completes. */
  unmountOnExit?: boolean;
  /** Animation duration in milliseconds. Defaults to 300ms. */
  timeout?: number;
  /** Collapse direction. Defaults to `'vertical'`. */
  orientation?: Orientation;
  /** Additional styles applied to the outer collapsing wrapper. */
  sx?: SxProps<Theme>;
  onEnter?: (isAppearing: boolean) => void;
  onEntering?: (isAppearing: boolean) => void;
  onEntered?: (isAppearing: boolean) => void;
  onExit?: () => void;
  onExiting?: () => void;
  onExited?: () => void;
}

export function SmoothCollapse({
  children,
  in: inProp = false,
  appear,
  enter,
  exit,
  mountOnEnter,
  unmountOnExit,
  timeout = DEFAULT_TIMEOUT_MS,
  orientation = 'vertical',
  sx,
  ...callbacks
}: SmoothCollapseProps) {
  const nodeRef = useRef<HTMLDivElement>(null);

  return (
    <Transition
      nodeRef={nodeRef}
      in={inProp}
      timeout={timeout}
      appear={appear}
      enter={enter}
      exit={exit}
      mountOnEnter={mountOnEnter}
      unmountOnExit={unmountOnExit}
      {...callbacks}
    >
      {(state: TransitionStatus) => {
        const isOpen = state === 'entering' || state === 'entered';

        const trackProperty =
          orientation === 'vertical' ? 'gridTemplateRows' : 'gridTemplateColumns';
        // The non-animating axis must be pinned to `minmax(0, 1fr)`. Without
        // it, the implicit `auto` track grows to fit children's min-content,
        // so wide/tall children (e.g. MUI Tabs `variant="scrollable"`) push
        // the collapse past its parent — the overflow then bubbles to the
        // document and produces a page-level scrollbar.
        const constrainProperty =
          orientation === 'vertical' ? 'gridTemplateColumns' : 'gridTemplateRows';
        const transitionProperty =
          orientation === 'vertical' ? 'grid-template-rows' : 'grid-template-columns';

        // While transitioning we must clip overflow so the content's natural size
        // doesn't bleed past the animating track. Once fully entered, switch to
        // `visible` so floating-label inputs, shadows, popovers, etc. can render
        // outside the content box (matches MUI Collapse's behavior).
        const overflow = state === 'entered' ? 'visible' : 'hidden';
        // Both `min-*: 0` are required: the animating axis needs it for the
        // collapse itself; the non-animating axis needs it so the grid item
        // can actually shrink to the parent-constrained track instead of
        // sticking at min-content.
        // Once fully collapsed the content is hidden, as MUI Collapse hides it:
        // a 0fr track only clips it, so its buttons and fields would stay in the
        // tab order and the accessibility tree while nobody can see them. Not in
        // the render where `in` turns true: the transition leaves 'exited' only
        // after that commit, which runs the opening content's autoFocus and
        // effects, and a hidden field cannot take focus (MUI's own condition).
        const innerStyles = {
          overflow,
          minWidth: 0,
          minHeight: 0,
          ...(state === 'exited' && !inProp ? { visibility: 'hidden' as const } : {}),
        };

        return (
          <Box
            ref={nodeRef}
            sx={[
              {
                display: 'grid',
                [trackProperty]: isOpen ? '1fr' : '0fr',
                [constrainProperty]: 'minmax(0, 1fr)',
                transition: `${transitionProperty} ${timeout}ms ${EASING}`,
              },
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              ...(Array.isArray(sx) ? sx : [sx]),
            ] as SxProps<Theme>}
          >
            <Box sx={innerStyles}>{children}</Box>
          </Box>
        );
      }}
    </Transition>
  );
}
